import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { appendMonth, setCurrentDate } from '../features/calendar/calendar-slice';
import { useAppDispatch } from '../app/store';
import { useAfterPaintEffect, useAppSelector } from '../app/hooks';
import { Month, Day } from '../helpers/common-types';
import { DATE_FORMAT, fillWeeksForGrid, getDateModifiers, isInView } from '../helpers/date-helpers';
import { useCurrentScrolledItem } from '../app/date-scroll-hook';
import { GridDay } from './grid-day';
import { MonthPagination } from './month-pagination';

export type GridProps = {
    filteredEvents: Month[];
}

export type DatePosition = {
    dateString: string;
    offsetTop: number;
    height: number;
}

export function GridLayout(props: GridProps) {
    const { filteredEvents } = props;
    const dispatch = useAppDispatch();
    const emptyMonthMessaging = useAppSelector(state => state.settings.emptyMonthMessaging);
    const today = useAppSelector(state => state.calendar.todaysDate);
    const currentDate = useAppSelector(state => state.calendar.currentDate);
    const filterBarHeight = useAppSelector(state => state.calendar.filterBarHeight);
    const [firstDayInRange, setFirstDayInRange] = useState('');
    const eventsHaveLoaded = useAppSelector(state => state.calendar.eventsHaveLoaded);

    const { currentScrolledItem } = useCurrentScrolledItem(firstDayInRange, filterBarHeight);

    useEffect(() => {
        setFirstDayInRange(getFirstDay());
    }, [filteredEvents]);

    /*
     * Set currentDate when scrolled to a new date (if it's in a different month)
     */
    useEffect(() => {
        if (currentScrolledItem !== '') {
            if (!dayjs(currentScrolledItem).isSame(currentDate, 'month')) {
                dispatch(setCurrentDate({ newDate: currentScrolledItem, shouldChangeRange: false }));
            }
        }
    }, [currentScrolledItem]);

    /*
     * Scroll to date when a currentDate is set outside of being scrolled to. On grid this should always be month name.
     */
    useAfterPaintEffect(() => {
        if (currentDate !== currentScrolledItem && eventsHaveLoaded) {
            // Grid view only has date keys on month names, at the start of the month
            const currentDateKey = document.querySelector(`[data-date="${dayjs(currentDate).startOf('month').format(DATE_FORMAT)}"]`);
            if (currentDateKey) {
                if (!isInView(currentDateKey)) {
                    currentDateKey.scrollIntoView({ block: "start" });
                    (currentDateKey as HTMLElement).focus({ preventScroll: true });
                }
            }
        }
    }, [currentDate, eventsHaveLoaded]);

    /*
     * Get first date of month as a scroll point
     */
    const getFirstDay = useCallback(() => {
        return filteredEvents ? filteredEvents[0].month : '';
    }, [filteredEvents]);

    return (
        <div className="ace-cal-grid-column">
            <div className="ace-cal-grid-months stack-xl">
                {
                    filteredEvents.map((monthData, monthIndex) => (
                        <section className="ace-cal-grid-month" key={monthIndex}>
                            <h2 id={`Month-Grid-${dayjs(monthData.month).format('DD-MM-YYYY')}`} className="ace-cal-grid-month-name" data-date={monthData.month} tabIndex={-1}>
                                {dayjs(monthData.month).format('MMMM YYYY')}
                            </h2>
                            {
                                monthData.anyEvents ?
                                    <ul className="ace-cal-grid-days">
                                        {
                                            fillWeeksForGrid(monthData.days).map((dayItem, dayIndex) => (
                                                <GridDay {...{ dayItem, dayIndex }} withinMonth={dayjs(dayItem.date).isSame(monthData.month, 'month')} modifiers={getDateModifiers(dayItem, today, currentDate, monthData.month, 'ace-cal-grid-day-')} />
                                            ))
                                        }
                                    </ul>
                                    :
                                    <div className="ace-cal-grid-days-empty">
                                        <div dangerouslySetInnerHTML={{ __html: emptyMonthMessaging }} />
                                    </div>
                            }
                        </section>
                    ))
                }
            </div>
            <MonthPagination />
        </div>
    )
}